<template>
    <div class="all-games">
        <div class="section-header">
            <div class="section-title">{{ title }}</div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="loading-spinner">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.3"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                    </path>
                </svg>
                <p>Loading games...</p>
            </div>
        </div>

        <div v-else class="games-grid" :class="{ 'large-cards-layout': largeCards }">
            <template v-for="game in games">
                <!-- ── Premium card for Crash & Mines ── -->
                <casino-game-card
                    v-if="game.name === 'Crash' || game.name === 'Mines'"
                    :key="game.id"
                    :game="game"
                    :bg-image="getGameBackgroundImage(game.name)"
                    :video-src="getGameVideoSrc(game.name)"
                    :disabled="isGameDisabled(game.name)"
                    :class="{ 'cgc-large': largeCards }"
                    @click="handleGameClick(game)"
                />

                <!-- ── Standard card for all other games ── -->
                <div v-else
                     class="game-card"
                     :key="game.id"
                     :class="{ 'game-disabled': isGameDisabled(game.name) }"
                     @click="handleGameClick(game)"
                     @mouseenter="onGameCardMouseEnter($event, game)"
                     @mouseleave="onGameCardMouseLeave($event, game)">
                    <!-- Original Game Display (image area + hover video) -->
                    <div v-if="game.type === 'original'" class="game-content game-top-section game-content-original" :style="{ backgroundImage: `url(${getGameBackgroundImage(game.name)})` }">
                        <video
                            v-if="getGameVideoSrc(game.name)"
                            class="game-card-hover-video"
                            muted
                            loop
                            playsinline
                            :src="getGameVideoSrc(game.name)"
                            preload="metadata"
                        />
                    </div>
                    <!-- Slot Game Display (with game image from API) -->
                    <div v-else-if="game.type === 'slot'" class="slot-game-wrapper game-top-section">
                        <img v-if="game.banner" :src="game.banner" :alt="game.name" class="slot-game-image" @error="handleImageError" />
                        <img v-else-if="game.image" :src="game.image" :alt="game.name" class="slot-game-image" @error="handleImageError" />
                        <div v-else class="slot-game-placeholder" :class="game.bgClass">
                            <span class="slot-game-name">{{ game.name.toUpperCase() }}</span>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <!-- Game Modal -->
        <transition name="game-modal-fade">
            <div v-if="shouldShowModal" class="game-modal-overlay" @click="closeGame" role="dialog" aria-modal="true" :aria-label="`Playing ${currentGameName}. Click outside or press Escape to close.`">
                <div class="game-modal" @click.stop role="document">
                    <div class="game-modal-header">
                        <h3>{{ currentGameName }}</h3>
                        <button type="button" class="close-btn" @click="closeGame" aria-label="Close game">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <span class="close-btn-text">Close</span>
                        </button>
                    </div>
                    <div class="game-modal-content">
                        <iframe 
                            :src="gameSession.launch_url" 
                            frameborder="0" 
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                    </div>
                    <button type="button" class="game-modal-close-floating" @click.stop="closeGame" aria-label="Close game">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import CasinoGameCard from '@/components/home/CasinoGameCard.vue';

export default {
    name: 'AllGames',
    components: { CasinoGameCard },
    props: {
        title: {
            type: String,
            default: ''
        },
        showTopBolts: {
            type: Boolean,
            default: true
        },
        showBottomBolts: {
            type: Boolean,
            default: true
        },
        showPlayButton: {
            type: Boolean,
            default: true
        },
        showSeeAll: {
            type: Boolean,
            default: true
        },
        filteredGameNames: {
            type: Array,
            default: () => []
        },
        largeCards: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            gameSession: null,
            currentGameName: '',
            allGames: [
                {
                    id: 1,
                    name: 'Crash',
                    subtitle: '',
                    route: '/crash',
                    bgClass: 'casino-bg',
                    type: 'original'
                },
                
                {
                    id: 5,
                    name: 'BlackJack',
                    subtitle: '',
                    route: '/blackjack',
                    bgClass: 'card4-bg',
                    type: 'original'
                },
                {
                    id: 2,
                    name: 'Mines',
                    subtitle: '',
                    route: '/mines',
                    bgClass: 'card2-bg',
                    type: 'original'
                },
                {
                    id: 4,
                    name: 'Towers',
                    subtitle: '',
                    route: '/towers',
                    bgClass: 'card4-bg',
                    type: 'original'
                },
                {
                    id: 6,
                    name: 'Duels',
                    subtitle: '',
                    route: '/duels',
                    bgClass: 'card4-bg',
                    type: 'original'
                },
            ]
        }
    },
    computed: {
        ...mapGetters(['authenticated']),
        shouldShowModal() {
            // Only show modal if we have a game session AND we're on the home page
            // Don't show on slots, casino, or other game pages
            const restrictedRoutes = ['/slots', '/our-casino', '/casino', '/live-casino'];
            const currentPath = this.$route.path;
            const isRestrictedRoute = restrictedRoutes.some(route => currentPath.includes(route));
            
            return this.gameSession && 
                   this.gameSession.launch_url && 
                   !isRestrictedRoute;
        },
        games() {
            // If filteredGameNames is provided, filter games by name (case-insensitive) and preserve order
            if (this.filteredGameNames && this.filteredGameNames.length > 0) {
                const filtered = [];
                this.filteredGameNames.forEach(filterName => {
                    const match = this.allGames.find(game => {
                        const gameNameLower = game.name.toLowerCase();
                        const filterNameLower = filterName.toLowerCase();
                        return gameNameLower === filterNameLower ||
                               gameNameLower === filterNameLower + 's' ||
                               gameNameLower + 's' === filterNameLower;
                    });
                    if (match) {
                        filtered.push(match);
                    }
                });
                return filtered;
            }
            // Otherwise return all games
            return this.allGames;
        }
    },
    watch: {
        '$route': {
            handler(newRoute) {
                // Close game modal when route changes OR on restricted routes
                const restrictedRoutes = ['/slots', '/our-casino', '/casino', '/live-casino'];
                const isRestrictedRoute = restrictedRoutes.some(route => newRoute.path.includes(route));
                
                if (this.gameSession && isRestrictedRoute) {
                    this.closeGame();
                }
            },
            immediate: true
        }
    },
    created() {
        // Ensure clean state on component creation
        this.gameSession = null;
        this.currentGameName = '';
        document.body.classList.remove('slot-game-playing');
        
        // Force close if on restricted route
        const restrictedRoutes = ['/slots', '/our-casino', '/casino', '/live-casino'];
        const isRestrictedRoute = restrictedRoutes.some(route => this.$route.path.includes(route));
        if (isRestrictedRoute && this.gameSession) {
            this.closeGame();
        }
    },
    mounted() {
        this.fetchSlotGamesData();
        document.addEventListener('keydown', this.handleModalKeydown);
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleModalKeydown);
        if (this.gameSession) {
            this.closeGame();
        }
    },
    methods: {
        ...mapActions(['notificationShow']),
        ...mapActions('slots', ['launchGameSeamless']),
        
        isGameDisabled(gameName) {
            const disabledGames = ['Upgrader', 'Duels', 'Battles', 'Unbox', 'Roll', 'Blackjack'];
            return disabledGames.includes(gameName);
        },
        
        getGameBackgroundImage(gameName) {
            const imageMap = {
                'Crash': require('@/assets/img/originals/crash.png'),
                'Mines': require('@/assets/img/originals/mines.png'),
                'Towers': require('@/assets/img/originals/tower.png'),
                'BlackJack': require('@/assets/img/originals/blackjack.png'),
                'Duels': require('@/assets/img/originals/duels.jpg')

            };
            return imageMap[gameName] || '';
        },

        getGameVideoSrc(gameName) {
            const videoMap = {
                'Crash': require('@/assets/original-videos/crash-video.mp4'),
                'BlackJack': require('@/assets/original-videos/blackjack-video.mp4'),
                'Mines': require('@/assets/original-videos/mine-video.mp4'),
                'Towers': require('@/assets/original-videos/tower-video.mp4'),
                'Duels': require('@/assets/original-videos/dice-video.mp4')
            };
            return videoMap[gameName] || '';
        },

        onGameCardMouseEnter(event, game) {
            if (game.type !== 'original') return;
            const card = event.currentTarget;
            const video = card.querySelector('.game-card-hover-video');
            if (video) {
                video.style.opacity = '1';
                video.play().catch(() => {});
            }
        },

        onGameCardMouseLeave(event, game) {
            if (game.type !== 'original') return;
            const card = event.currentTarget;
            const video = card.querySelector('.game-card-hover-video');
            if (video) {
                video.pause();
                video.style.opacity = '0';
            }
        },
        
        handleModalKeydown(e) {
            if (e.key === 'Escape' && this.shouldShowModal) {
                this.closeGame();
            }
        },
        async handleGameClick(game) {
            // Don't navigate if game is disabled
            if (this.isGameDisabled(game.name)) {
                return;
            }

            // For slot games, launch in modal
            if (game.type === 'slot') {
                await this.launchSlotGame(game);
            } else {
                // For original games, navigate to their route
                this.navigateToGame(game.route);
            }
        },

        async launchSlotGame(game) {
            if (!this.authenticated) {
                this.notificationShow({ type: 'error', message: 'Please sign in to play slots.' });
                this.$root.$emit('open-signin-modal-app');
                return;
            }

            // Check if we have provider_code and code from API
            if (!game.provider_code || !game.code) {
                this.notificationShow({ 
                    type: 'error', 
                    message: 'Game data not available. Please try again later.' 
                });
                return;
            }

            try {
                this.currentGameName = game.name;
                
                // Launch the game using the Vuex store action
                const session = await this.launchGameSeamless({
                    providerCode: game.provider_code,
                    gameCode: game.code
                });

                if (session) {
                    this.gameSession = session;
                    document.body.classList.add('slot-game-playing');
                } else {
                    this.notificationShow({ 
                        type: 'error', 
                        message: 'Failed to launch game. Please try again.' 
                    });
                }
            } catch (error) {
                console.error('Error launching game:', error);
                this.notificationShow({ 
                    type: 'error', 
                    message: error.message || 'Failed to launch game. Please try again.' 
                });
            }
        },

        closeGame() {
            // Clear game session data
            this.gameSession = null;
            this.currentGameName = '';
            
            // Remove body class
            document.body.classList.remove('slot-game-playing');
            
            // Force remove any lingering iframes
            this.$nextTick(() => {
                const iframes = document.querySelectorAll('.game-modal-content iframe');
                iframes.forEach(iframe => {
                    if (iframe.parentNode) {
                        iframe.src = 'about:blank';
                        iframe.parentNode.removeChild(iframe);
                    }
                });
            });
        },

        navigateToGame(route) {
            this.$router.push(route);
        },
        
        handleImageError(event) {
            // Hide broken image and show fallback
            event.target.style.display = 'none';
        },
        
        async fetchSlotGamesData() {
            this.loading = true;
            try {
                // Fetch games from INOUT provider only
                const providers = ['INOUT'];
                const slotGamesMap = {
                    'Chicken': { searchTerm: 'chicken road two', provider: 'INOUT' }
                };
                
                const gamesPromises = providers.map(provider => 
                    axios.get(`/slots/games/${provider}`)
                        .then(res => {
                            if (res.data.success && res.data.data) {
                                return { provider, games: res.data.data };
                            }
                            return { provider, games: [] };
                        })
                        .catch(err => {
                            console.warn(`Failed to fetch games for ${provider}:`, err.message);
                            return { provider, games: [] };
                        })
                );
                
                const providerGames = await Promise.all(gamesPromises);
                
                // Update slot games with API data
                this.allGames.forEach(game => {
                    if (game.type === 'slot' && slotGamesMap[game.name]) {
                        const gameInfo = slotGamesMap[game.name];
                        const providerData = providerGames.find(p => p.provider === gameInfo.provider);
                        
                        if (providerData && providerData.games.length > 0) {
                            // Search for the game by name (case insensitive)
                            const apiGame = providerData.games.find(g => 
                                g.name && g.name.toLowerCase().includes(gameInfo.searchTerm.toLowerCase())
                            );
                            
                            if (apiGame) {
                                game.banner = apiGame.banner;
                                game.image = apiGame.image;
                                game.provider_code = apiGame.provider_code;
                                game.code = apiGame.code;
                                // Preserve the original game name from allGames array
                                // Don't use apiGame.name
                            }
                        }
                    }
                });
                
            } catch (error) {
                console.error('Error fetching slot games:', error);
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style scoped>
.all-games {
    padding: var(--spacing-xl) 0 var(--spacing-xl) 0;
    margin: 0 21px 0 21px;
}

@media only screen and (max-width: 768px) {
    .all-games {
        padding: var(--spacing-md);
        margin: 0;
    }
}

.section-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 var(--spacing-xl) 0;
    padding: var(--spacing-lg) 0;
}

.section-title {
    font-size: 32px;
    font-weight: 900;
    text-align: left;
    margin: 0;
    background: var(--accent-main-txt);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 2px;
    position: relative;
    display: inline-block;
}

.see-all-link {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;
}

.see-all-link:hover {
    color: var(--accent-btn-purple);
    transform: translateX(2px);
}

.see-all-link svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    fill: none;
    transition: transform 0.3s ease;
}

.see-all-link:hover svg {
    transform: translateX(2px);
}

/* Copper bolts removed */

/* Loading State */
.loading-state {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
}

.loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.loading-spinner svg {
    color: var(--accent-yellow);
}

.loading-spinner p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
}

/* Games Grid */
.games-grid {
    display: flex;
    gap: 28px;
    max-width: 1400px;
    margin: 0 auto;
    flex-wrap: wrap;
    justify-content: center;
}

/* Large cards layout */
.games-grid.large-cards-layout {
    gap: 32px;
    max-width: 1600px;
    justify-content: center;
}

.games-grid.large-cards-layout .game-card {
    width: 260px;
    height: 280px;
}

.game-card {
    border-radius: 12px 12px 16px 16px;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    cursor: pointer;
    width: 166px;
    height: 222px;
    display: flex;
    flex-direction: column;
    background: #1a1a2e;
    border: 2px solid #2d2d44;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.game-card:hover:not(.game-disabled) {
    transform: translateY(-6px) scale(1.05);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
    border-color: #3d3d54;
}

/* Disabled game styles */
.game-card.game-disabled {
    cursor: not-allowed;
    opacity: 0.7;
}

.game-card.game-disabled:hover {
    transform: none;
}

/* Top section: image/graphic area (~70–75% of card) – bit dark by default, bright on hover */
.game-content.game-top-section {
    flex: 1;
    min-height: 0;
    margin: 0;
    padding: 0;
    border-radius: 0;
    display: block;
    position: relative;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: brightness(0.88);
    transition: filter 0.35s ease;
}

.game-card:hover:not(.game-disabled) .game-content.game-top-section {
    filter: brightness(1.2);
}

.game-content.game-top-section::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.06), rgba(0, 0, 0, 0.22));
    pointer-events: none;
    z-index: 1;
    transition: opacity 0.35s ease;
}

.game-card:hover:not(.game-disabled) .game-content.game-top-section::before {
    opacity: 0.4;
}

/* Original game card: hover video overlay */
.game-content.game-content-original {
    overflow: hidden;
}

.game-card-hover-video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    z-index: 2;
    pointer-events: none;
    transition: opacity 0.3s ease;
}

/* Background styles for each game */
.game-content.casino-bg {
    background: linear-gradient(135deg, #1e8449 0%, #27ae60 50%, #2ecc71 100%);
}

.game-content.card2-bg {
    background: linear-gradient(135deg, #1e8449 0%, #27ae60 50%, #2ecc71 100%);
}

.game-content.card3-bg {
    background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), 
                url('~@/assets/img/gamePreview/upgrader.png');
    background-size: cover;
    background-position: center;
}

.game-content.card4-bg {
    background: linear-gradient(135deg, #1a5276 0%, #2980b9 50%, #3498db 100%);
}

.game-content.card5-bg {
    background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), 
                url('~@/assets/img/gamePreview/black_jack.png');
    background-size: cover;
    background-position: center;
}

.game-content.card6-bg {
    background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), 
                url('~@/assets/img/gamePreview/duels.jpg');
    background-size: cover;
    background-position: center;
}

.game-content.card7-bg {
    background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), 
                url('~@/assets/img/gamePreview/battles.jpg');
    background-size: cover;
    background-position: center;
}

.game-content.card8-bg {
    background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), 
                url('~@/assets/img/gamePreview/unbox.jpg');
    background-size: cover;
    background-position: center;
}

.game-content.card9-bg {
    background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), 
                url('~@/assets/img/gamePreview/roll.jpeg');
    background-size: cover;
    background-position: center;
}

/* Slot Game Styles */
.slot-game-wrapper.game-top-section {
    flex: 1;
    min-height: 0;
    margin: 0;
    border-radius: 0;
    overflow: hidden;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: radial-gradient(circle at 20% 10%, rgba(59, 130, 246, 0.24), rgba(8, 12, 22, 1) 66%);
}

.slot-game-image {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    object-position: center;
    display: block;
    filter: brightness(0.88);
    transition: filter 0.35s ease;
}

.game-card:hover:not(.game-disabled) .slot-game-image {
    filter: brightness(1.2);
}

.slot-game-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.slot-game-name {
    font-size: 22px;
    font-weight: 900;
    color: #ffffff;
    text-transform: uppercase;
    text-align: center;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.6);
    letter-spacing: 1.5px;
    line-height: 1.3;
}

/* Slot background classes for fallback */
.slot-game-placeholder.slot1-bg {
    background: linear-gradient(135deg, #1e8449 0%, #27ae60 50%, #2ecc71 100%);
}

/* Title overlay at bottom of image area */
.game-title-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px 12px 14px;
    text-align: center;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.75), transparent);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 2;
}

.game-info.game-title-overlay {
    flex: none;
}

.game-title {
    font-size: 20px;
    font-weight: 900;
    color: #f4d03f;
    text-transform: uppercase;
    margin: 0;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
    letter-spacing: 1.2px;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

/* Bottom section: full-width purple bar (~25–30%) */
.play-button {
    background: var(--accent-btn-purple);
    border: none;
    border-radius: 0 0 16px 16px;
    color: #ffffff;
    cursor: pointer;
    font-size: 15px;
    font-weight: 800;
    padding: 14px 18px;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    width: 100%;
    margin: 0;
    text-align: center;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.play-button:hover {
    background: var(--gradient-purple);
}

.game-card.game-disabled .play-button {
    cursor: not-allowed;
}

/* Responsive styles */
@media (max-width: 1400px) {
    .games-grid.large-cards-layout {
        gap: 28px;
    }
    
    .games-grid.large-cards-layout .game-card {
        width: 240px;
        height: 260px;
    }
}

@media (max-width: 1200px) {
    .games-grid {
        justify-content: center;
        gap: 24px;
    }
    
    .games-grid.large-cards-layout .game-card {
        width: 220px;
        height: 250px;
    }
}

@media (max-width: 768px) {
    .games-grid {
        gap: 20px;
        justify-content: center;
    }
    
    .game-card {
        width: 180px;
        height: 220px;
    }
    
    .games-grid.large-cards-layout .game-card {
        width: 200px;
        height: 240px;
    }
    
    .section-title {
        font-size: 22px;
    }
    
    .game-title {
        font-size: 16px;
    }
    
    .game-title-overlay {
        padding: 12px 8px 10px;
    }
    
    .slot-game-placeholder {
        padding: 16px;
    }
    
    .slot-game-name {
        font-size: 18px;
    }
    
    .play-button {
        font-size: 13px;
        padding: 12px 14px;
        height: 46px;
        border-radius: 0 0 14px 14px;
    }
}

@media only screen and (max-width: 480px) {
    .game-card {
        width: 160px;
        height: 200px;
    }
    
    .game-title {
        font-size: 14px;
    }
    
    .game-title-overlay {
        padding: 10px 6px 8px;
    }
    
    .slot-game-placeholder {
        padding: 14px;
    }
    
    .slot-game-name {
        font-size: 16px;
    }
    
    .play-button {
        font-size: 12px;
        padding: 10px 12px;
        height: 42px;
        border-radius: 0 0 12px 12px;
    }
}

/* Game Modal */
.game-modal-fade-enter-active,
.game-modal-fade-leave-active {
    transition: opacity 0.25s ease;
}
.game-modal-fade-enter-active .game-modal,
.game-modal-fade-leave-active .game-modal {
    transition: transform 0.25s ease;
}
.game-modal-fade-enter-from,
.game-modal-fade-leave-to {
    opacity: 0;
}
.game-modal-fade-enter-from .game-modal,
.game-modal-fade-leave-to .game-modal {
    transform: scale(0.98);
}

/* Slot game modal: full screen (covers entire viewport) */
.game-modal-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    background: #000;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    z-index: 9999999 !important;
    padding: 0 !important;
    margin: 0 !important;
    box-sizing: border-box !important;
}

.game-modal {
    background: #000;
    border: none;
    border-radius: 0;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    z-index: 10000000 !important;
}

.game-modal .close-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    min-width: 44px;
    min-height: 44px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    transition: all 0.2s ease;
}
.game-modal .close-btn:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
}
.game-modal .close-btn:focus-visible {
    outline: 2px solid var(--color-copper);
    outline-offset: 2px;
}
.game-modal .close-btn-text {
    display: none;
}
@media (min-width: 480px) {
    .game-modal .close-btn-text { display: inline; }
}

.game-modal-close-floating {
    position: absolute !important;
    top: 12px !important;
    right: 12px !important;
    width: 48px !important;
    height: 48px !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
    background: rgba(0, 0, 0, 0.6) !important;
    border: 2px solid rgba(255, 255, 255, 0.25) !important;
    border-radius: 50% !important;
    color: #fff !important;
    cursor: pointer !important;
    z-index: 10000003 !important;
    transition: all 0.2s ease;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}
.game-modal-close-floating:hover {
    background: rgba(0, 0, 0, 0.85) !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
    transform: scale(1.05);
}

.game-modal-header {
    display: none !important;
}

.game-modal-header h3 {
    font-size: 20px;
    font-weight: 700;
    color: #ffffff;
    margin: 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.game-modal-content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    background: #000;
    position: relative;
}
.game-modal-content iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
    display: block;
}

/* Better z-index management */
body.slot-game-playing {
    overflow: hidden !important;
}

@media (max-width: 1024px) {
    .game-modal-overlay {
        padding: 0 !important;
    }
}

@media (max-width: 768px) {
    .game-modal-overlay {
        padding: 0 !important;
    }
}

@media only screen and (max-width: 480px) {
    .game-modal-overlay {
        padding: 0 !important;
    }
}
</style>
